import { businessContext } from '@/business/business';
import {
  ANNOTATION_COLUMN,
  ExploreLayoutColumn,
  SCENE_COLUMN,
  VIEWER_COLUMN,
} from './ExploreLayoutColumn';
import { ExploreLayoutTab, MOBILE_TABS, SCENE_TAB, VIEWER_TAB } from './ExploreLayoutTab';

export interface MobileExploreLayoutDto {
  columns: ExploreLayoutColumn[];
  currentColumn: ExploreLayoutColumn;
  currentTab: ExploreLayoutTab;
  tabs: ExploreLayoutTab[];
}

class MobileExploreLayout {
  private _businessContext = businessContext;
  private _setValue = this._businessContext.attributeStore._setValue;

  columns = businessContext.attributeStore.mutable([SCENE_COLUMN, VIEWER_COLUMN]);
  bedrockColumn = businessContext.attributeStore.mutable<ExploreLayoutColumn>(VIEWER_COLUMN);
  columnStack = businessContext.attributeStore.mutable<ExploreLayoutColumn[]>([]);
  tabs = businessContext.attributeStore.mutable(MOBILE_TABS);
  isViewerScreenshotEnabled = businessContext.attributeStore.mutable(false);

  currentColumn = businessContext.attributeStore.derivable(get => {
    const bedrockColumn = get(this.bedrockColumn);
    const columnStack = get(this.columnStack);
    if (columnStack.length === 0) return bedrockColumn;
    return columnStack[columnStack.length - 1];
  });

  currentTab = businessContext.attributeStore.derivable(get => {
    const currentColumn = get(this.currentColumn);
    if (currentColumn === SCENE_COLUMN) return SCENE_TAB;
    if (currentColumn === VIEWER_COLUMN) return VIEWER_TAB;
    return null;
  });

  unmountCurrentColumn = () => {
    const currentColumn = this.currentColumn.getValue();
    currentColumn.widgets.forEach(widget => {
      widget.unmount?.();
    });
  };

  switchTab = (nextTab: ExploreLayoutTab) => {
    const currentTab = this.currentTab.getValue();
    if (currentTab === nextTab) return;

    this.popAllColumns();
    this.unmountCurrentColumn();

    if (nextTab === SCENE_TAB) {
      this._setValue(this.bedrockColumn, SCENE_COLUMN);
    } else if (nextTab === VIEWER_TAB) {
      this._setValue(this.bedrockColumn, VIEWER_COLUMN);
    }
  };

  pushColumn = (column: ExploreLayoutColumn) => {
    // Viewer has an issue that calls startUpdateAnnotation twice when clicking on annotation in viewer.
    // (in handleCursorTap & in useEffect in <Viewer />)
    // We do deduplicate for now, but in the future we might want to allow stacking columns of one type.
    const currentColumn = this.currentColumn.getValue();
    if (currentColumn === column) return;
    this._setValue<ExploreLayoutColumn[]>(this.columnStack, prev => [...prev, column]);
  };

  popColumn = () => {
    const currentColumn = this.currentColumn.getValue();
    currentColumn.widgets.forEach(widget => {
      widget.unmount?.();
    });

    this._setValue<ExploreLayoutColumn[]>(this.columnStack, prev => {
      const newStack = [...prev];
      newStack.pop();
      return newStack;
    });
  };

  popAllColumns = () => {
    let columnStack = this.columnStack.getValue();
    while (columnStack.length > 0) {
      this.popColumn();
      columnStack = this.columnStack.getValue();
    }
  };

  focusAnnotationEdit = () => {
    const columnStack = this.columnStack.getValue();
    if (!columnStack.includes(ANNOTATION_COLUMN)) this.pushColumn(ANNOTATION_COLUMN);
  };

  unfocusAnnotationEdit = () => {
    const currentColumn = this.currentColumn.getValue();
    if (currentColumn === ANNOTATION_COLUMN) this.popColumn();
  };

  startEditAnnotationFromAnnotationEdit = () => {
    const currentColumn = this.currentColumn.getValue();
    if (currentColumn === ANNOTATION_COLUMN) this.switchTab(VIEWER_TAB);
  };

  flyTo = () => {
    const currentColumn = this.currentColumn.getValue();
    if (currentColumn !== VIEWER_COLUMN) this.switchTab(VIEWER_TAB);
  };

  startPhotoOverlay = () => {
    const currentColumn = this.currentColumn.getValue();
    if (currentColumn !== VIEWER_COLUMN) this.switchTab(VIEWER_TAB);
  };

  startPanoramaWalkthrough = () => {
    const currentColumn = this.currentColumn.getValue();
    if (currentColumn !== VIEWER_COLUMN) this.switchTab(VIEWER_TAB);
  };

  completeViewerScreenshot: null | ((base64: string) => void) = null;
  startViewerScreenshot = async () => {
    const currentColumn = this.currentColumn.getValue();
    if (currentColumn !== VIEWER_COLUMN) this.pushColumn(VIEWER_COLUMN);
    this._setValue(this.isViewerScreenshotEnabled, true);

    return new Promise<string>(resolve => {
      this.completeViewerScreenshot = (base64: string) => {
        this.stopViewerScreenshot();
        resolve(base64);
      };
    });
  };

  stopViewerScreenshot = () => {
    const currentColumn = this.currentColumn.getValue();
    if (currentColumn === VIEWER_COLUMN) this.popColumn();
    this._setValue(this.isViewerScreenshotEnabled, false);
    this.completeViewerScreenshot = null;
  };
}

export const exploreLayout = new MobileExploreLayout();
